/**
 * Встречи (DEV-1947, вынесены в свою фичу DEV-1973): общий протокол созвона.
 *
 * Встреча рождается первым приглашением, а не входом в мит-режим: человек,
 * который развернул ленту и пишет один, встречи не начинает. Участник — тот,
 * кто пришёл: приглашение само по себе заметок не открывает.
 *
 * События встречи приезжают потоком NDJSON — той же дорогой, что и ответ
 * ассистента, и по той же причине. Строка потока несёт только вид события,
 * дальше клиент перечитывает ленту: склеенная из кусочков она разошлась бы с
 * серверной, а перечитанная согласована всегда.
 */
import { planApi } from '@/api'
import { ensureFreshToken } from '@/api/http'

import { readNdjson } from '@/features/assistant/stream'

import type {
  TrackerMeetingInvite,
  TrackerMeetingSession,
  TrackerMeetingState,
} from '@/api/clients/tracker-plan'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

/**
 * Сколько встреч показываем в списке.
 *
 * Полсотни — это больше квартала созвонов; дальше нужен уже поиск, а не
 * прокрутка, и подгрузка страницами ради этого не окупается.
 */
const MEETINGS_PAGE = 50

/** Участник встречи в том виде, в каком его рисует шапка мит-режима */
export interface MeetingParticipant {
  userId: string
  name: string
  avatarUrl: string
  /** Пришёл ли. Позванный, но не пришедший, заметок не видит */
  joined: boolean
}

export interface MeetingSession {
  id: string
  authorId: string
  /** Завершить встречу может только автор */
  iAmAuthor: boolean
  createdAt: string
  participants: MeetingParticipant[]
  /** Идёт ли она сейчас. Законченная остаётся читаемой — это архив */
  active: boolean
  /** Сколько в ней заметок: список говорит это, не поднимая саму ленту */
  noteCount: number
}

export interface MeetingInvite {
  sessionId: string
  invitedByName: string
  invitedAt: string
}

export interface MeetingState {
  session: MeetingSession | null
  invites: MeetingInvite[]
}

const toSession = (session: TrackerMeetingSession): MeetingSession => ({
  id: session.id ?? '',
  authorId: session.authorId ?? '',
  iAmAuthor: session.iAmAuthor ?? false,
  createdAt: session.createdAt ?? '',
  participants: (session.participants ?? []).map((participant) => ({
    userId: participant.userId ?? '',
    name: participant.displayName ?? '',
    avatarUrl: participant.avatarUrl ?? '',
    joined: participant.joined ?? false,
  })),
  // Состояние встречи приезжает только списком и карточкой: у баннера его нет,
  // и там она всегда идущая — иначе на неё бы не звали.
  active: session.active ?? true,
  noteCount: session.noteCount ?? 0,
})

const toInvite = (invite: TrackerMeetingInvite): MeetingInvite => ({
  sessionId: invite.sessionId ?? '',
  invitedByName: invite.invitedByName ?? '',
  invitedAt: invite.invitedAt ?? '',
})

const toState = (state: TrackerMeetingState): MeetingState => ({
  session: state.session?.id ? toSession(state.session) : null,
  invites: (state.invites ?? []).map(toInvite),
})

export const getMeetingState = async (): Promise<MeetingState> => {
  const { data } = await planApi.planServiceGetMeetingState()

  return toState(data)
}

export const inviteToMeeting = async (userIds: string[]): Promise<MeetingState> => {
  const { data } = await planApi.planServiceInviteToMeeting({ userIds })

  return toState(data)
}

export const joinMeeting = async (sessionId: string): Promise<MeetingState> => {
  const { data } = await planApi.planServiceJoinMeeting({ sessionId })

  return toState(data)
}

export const endMeeting = async (sessionId: string): Promise<void> => {
  await planApi.planServiceEndMeeting({ sessionId })
}

/**
 * Встречи, где человек был: идущая первой, прошлые за ней (DEV-1971).
 *
 * Заметок список не несёт — их читает лента с идентификатором встречи. Иначе
 * открытие экрана поднимало бы всю историю созвонов разом.
 */
export const listMeetings = async (limit = MEETINGS_PAGE): Promise<MeetingSession[]> => {
  const { data } = await planApi.planServiceListMeetings('1', String(limit))

  return (data.meetings ?? []).map(toSession)
}

/** Одна встреча, в том числе законченная: её открывают ссылкой из списка */
export const getMeeting = async (sessionId: string): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceGetMeeting(sessionId)

  return toSession(data)
}

/** Виды событий потока; повторяют `dto.MeetingEventKind` бэкенда */
export type MeetingEventKind =
  | 'open'
  | 'ping'
  | 'note'
  | 'participants'
  | 'ended'
  | 'crdt'
  | 'crdt-gap'
  | 'awareness'

/**
 * Событие потока.
 *
 * Сигнальные виды несут только `kind` — клиент по ним перечитывает ленту.
 * Правки и присутствие (DEV-1972) приезжают телом: правку нельзя заменить
 * сигналом «перечитай» — перечитанная лента отдаст готовый текст и потеряет
 * то, ради чего всё затевалось.
 */
export interface MeetingEvent {
  kind: MeetingEventKind
  noteId?: string
  /** Номер правки в журнале заметки: по разрыву виден пропуск */
  seq?: number
  actorId?: string
  /** Тело правки или присутствия; на проводе — base64 */
  payload?: string
}

interface MeetingStreamOptions {
  signal: AbortSignal
  onEvent: (event: MeetingEvent) => void
}

/**
 * Поток событий одной встречи. Живёт, пока открыт мит-режим.
 *
 * Обрыв тут — обычное дело: выкат сервиса, спящий ноутбук, метро. Поэтому
 * функция просто заканчивается, а переподключение и догон пропущенного —
 * забота стора: только он знает, что именно перечитывать.
 */
export const streamMeeting = async (
  sessionId: string,
  options: MeetingStreamOptions,
): Promise<void> => {
  const response = await fetch(
    `${baseURL}/api/v1/tracker/plan/meeting/${sessionId}/stream`,
    {
      headers: { Authorization: `Bearer ${await ensureFreshToken()}` },
      signal: options.signal,
    },
  )

  if (!response.ok || !response.body) {
    throw new Error(`meeting stream: ${response.status}`)
  }

  await readNdjson<MeetingEvent & { note_id?: string; actor_id?: string }>(
    response.body,
    (event) => {
      options.onEvent({
        kind: event.kind ?? 'ping',
        // Ключи на проводе snake_case, как во всём JSON трекера.
        noteId: event.note_id,
        actorId: event.actor_id,
        seq: event.seq,
        payload: event.payload,
      })
    },
  )
}
